<template>
  <div class="container">
    <!-- 筛选栏 -->
    <div class="filter-container">
      <div class="filter-row">
        <div class="filter-item">
          <label class="filter-label">选择年度</label>
          <van-field
            v-model="currentYear"
            is-link
            readonly
            placeholder="请选择"
            @click="showYearPicker = true"
          />
        </div>
        <div class="filter-item">
          <label class="filter-label">预算类别</label>
          <van-field
            v-model="categoryName"
            is-link
            readonly
            placeholder="请选择"
            @click="showCategoryPicker = true"
          />
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-container">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ formatMoney(totalBudget) }}</div>
          <div class="stat-label">年度预算</div>
          <div class="stat-diff" :class="budgetDiffClass">
            较上年 {{ summaryData.budgetYearOnYearRatio }}
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ formatMoney(totalActual) }}</div>
          <div class="stat-label">实际支出</div>
          <div class="stat-diff" :class="actualDiffClass">
            占预算 {{ summaryData.actualToBudgetRatio }}
          </div>
        </div>
      </div>
    </div>

    <!-- 类型对比列表 -->
    <div class="category-list">
      <h2 class="section-title">{{ currentYear }}年度支出类型对比</h2>
      <van-loading v-if="loading" class="loading" />
      <div v-else-if="filteredList.length === 0" class="empty-state">
        <van-icon name="description" size="48" color="#999" />
        <div class="empty-text">暂无数据</div>
        <div class="empty-hint">请尝试调整筛选条件</div>
      </div>
      <div v-else>
        <div
          class="category-card"
          v-for="(item, index) in filteredList"
          :key="index"
        >
          <div class="category-header">
            <div class="category-name">{{ item.name }}</div>
          </div>
          <table class="comparison-table">
            <tr>
              <td class="text-left">预算金额</td>
              <td class="budget-amount">{{ formatMoney(item.budget) }}</td>
            </tr>
            <tr>
              <td class="text-left">实际支出</td>
              <td class="actual-amount">{{ formatMoney(item.actual) }}</td>
            </tr>
            <tr>
              <td class="text-left">差异</td>
              <td :class="['diff-amount', item.diff >= 0 ? 'positive' : 'negative']">
                {{ item.diff >= 0 ? '+' : '' }}{{ formatMoney(Math.abs(item.diff)) }}
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <!-- 年度选择器 -->
    <van-popup v-model="showYearPicker" position="bottom">
      <van-picker
        show-toolbar
        title="选择年度"
        :columns="yearColumns"
        :default-index="defaultYearIndex"
        @confirm="onYearConfirm"
        @cancel="showYearPicker = false"
      />
    </van-popup>

    <!-- 类别选择器 -->
    <van-popup v-model="showCategoryPicker" position="bottom">
      <van-picker
        show-toolbar
        title="选择类别"
        :columns="categoryColumns"
        @confirm="onCategoryConfirm"
        @cancel="showCategoryPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import axios from '@/utils/request'

export default {
  name: 'Ysgs',
  data() {
    return {
      loading: true,
      currentYear: String(new Date().getFullYear()),
      categoryName: '全部类型',
      category: null,  // 选中的类别ID，null表示全部
      showYearPicker: false,
      showCategoryPicker: false,
      // 年度选项
      yearOptions: [],
      // 类别选项（从接口获取）
      categoryOptions: [
        { value: null, label: '全部类型' }
      ],
      // 统计汇总数据
      summaryData: {
        totalBudget: 0,
        totalActual: 0,
        budgetYearOnYearRatio: '0.0%',
        actualToBudgetRatio: '0.0%',
        categories: []
      }
    }
  },
  computed: {
    yearColumns() {
      return this.yearOptions
    },
    defaultYearIndex() {
      return this.yearOptions.indexOf(this.currentYear)
    },
    categoryColumns() {
      return this.categoryOptions.map(item => item.label)
    },
    // 根据筛选条件过滤类别列表
    filteredList() {
      let list = this.summaryData.categories.map(item => ({
        categoryId: item.categoryId,
        name: item.categoryName,
        budget: item.budgetAmount,
        actual: item.actualAmount,
        diff: item.difference
      }))
      if (this.category !== null) {
        list = list.filter(item => item.categoryId === this.category)
      }
      return list
    },
    totalBudget() {
      return this.summaryData.totalBudget
    },
    totalActual() {
      return this.summaryData.totalActual
    },
    budgetDiff() {
      // 解析同比增长率，去掉百分号和正负号
      const ratio = this.summaryData.budgetYearOnYearRatio
      if (!ratio || ratio === '0.0%') return 0
      return parseFloat(ratio.replace('%', ''))
    },
    actualDiff() {
      // 解析支出预算比，计算差异（实际支出相对预算的百分比）
      const ratio = this.summaryData.actualToBudgetRatio
      if (!ratio || ratio === '0.0%') return 0
      // 实际支出与预算的差异百分比 = 支出预算比 - 100%
      return (parseFloat(ratio.replace('%', '')) - 100).toFixed(1)
    },
    // 预算同比增长率样式类
    budgetDiffClass() {
      const ratio = this.summaryData.budgetYearOnYearRatio
      if (!ratio || ratio === '0.0%') return ''
      if (ratio.startsWith('+')) return 'positive'
      if (ratio.startsWith('-')) return 'negative'
      return ''
    },
    // 支出预算比样式类（超过100%为超支红色，低于为结余绿色）
    actualDiffClass() {
      const ratio = this.summaryData.actualToBudgetRatio
      if (!ratio || ratio === '0.0%') return ''
      const value = parseFloat(ratio.replace('%', ''))
      if (value > 100) return 'negative'  // 超支
      if (value < 100) return 'positive'  // 结余
      return ''
    }
  },
  mounted() {
    this.initPage()
  },
  methods: {
    async initPage() {
      // 初始化年度选项
      this.initYearOptions()
      // 并行加载数据
      await Promise.all([
        this.loadCategories(),
        this.loadSummary()
      ])
      this.loading = false
    },
    // 初始化年度选项（当前年份往前推5年）
    initYearOptions() {
      const currentYear = new Date().getFullYear()
      for (let i = 0; i < 5; i++) {
        this.yearOptions.push(String(currentYear - i))
      }
    },
    // 加载预算类别下拉
    async loadCategories() {
      try {
        const res = await axios.get('/api/v1/budget/categories')
        if (res && Array.isArray(res)) {
          this.categoryOptions = [
            { value: null, label: '全部类型' },
            ...res.map(item => ({
              value: item.value,
              label: item.label
            }))
          ]
        }
      } catch (error) {
        console.error('加载预算类别失败:', error)
      }
    },
    // 加载预算统计汇总
    async loadSummary() {
      try {
        const res = await axios.get('/api/v1/budget/summary', {
          params: { year: this.currentYear }
        })
        if (res) {
          this.summaryData = {
            totalBudget: res.totalBudget || 0,
            totalActual: res.totalActual || 0,
            budgetYearOnYearRatio: res.budgetYearOnYearRatio || '0.0%',
            actualToBudgetRatio: res.actualToBudgetRatio || '0.0%',
            categories: res.categories || []
          }
        }
      } catch (error) {
        console.error('加载预算统计失败:', error)
      }
    },
    formatMoney(value) {
      if (!value && value !== 0) return '¥0'
      return '¥' + value.toLocaleString()
    },
    async onYearConfirm(value, index) {
      this.currentYear = value
      this.showYearPicker = false
      this.loading = true
      await this.loadSummary()
      this.loading = false
    },
    onCategoryConfirm(value, index) {
      this.categoryName = value
      this.category = this.categoryOptions[index].value
      this.showCategoryPicker = false
    }
  }
}
</script>

<style lang="less" scoped>
.container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.filter-container {
  padding: 16px;
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
}

.filter-row {
  display: flex;
  gap: 12px;
}

.filter-item {
  flex: 1;
}

.filter-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  display: block;
}

.stats-container {
  padding: 16px;
  background-color: #f5f5f5;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stat-card {
  background-color: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.stat-diff {
  font-size: 12px;
  margin-top: 4px;
}

.stat-diff.positive {
  color: #52c41a;
}

.stat-diff.negative {
  color: #ff4d4f;
}

.category-list {
  padding: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.loading {
  text-align: center;
  padding: 32px 16px;
}

.empty-state {
  text-align: center;
  padding: 48px 16px;
  color: #999;
}

.empty-text {
  font-size: 16px;
  margin: 16px 0 8px;
}

.empty-hint {
  font-size: 14px;
}

.category-card {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table td {
  padding: 8px;
  text-align: right;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.text-left {
  text-align: left;
  color: #666;
}

.budget-amount {
  color: #1890ff;
}

.actual-amount {
  color: #52c41a;
}

.diff-amount.positive {
  color: #52c41a;
}

.diff-amount.negative {
  color: #ff4d4f;
}

/deep/ .van-field {
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fff;
}
</style>
